import { BaseWebComponentView } from '../../../framework/BaseWebComponentView';
import { GeneralDonutChartInput } from '../../../ui/gzGeneralDonutChart/types';
import { WidgetStateFrontEnd } from '../../../genericDashboard/gzGenericWidget/types';
import { htmlEncode } from '../../../utils/escape.utils';
import {
  CLOUD_SECURITY_NUMBER_OF_ITEMS,
  CloudSecuritySectionEvents,
  ERA_NUMBER_OF_ITEMS,
  ErrorCodes,
  RisksSections,
  RisksSectionsStrings,
  ROOT_CAUSE_NUMBER_OF_ITEMS,
  SectionsItems,
} from '../types';
import { templateErrorLoadingChart, templateNoDataAvailableChart } from './template';
import { GzTextWebComponent } from '../../../ui/gzText';
import { BaseWebComponentTranslator } from '../../../framework/BaseWebComponentTranslator';
import { ErrorInterface } from '../../riskIncidentOverviewSection/types';

export class RiskIncidentPanelWebView extends BaseWebComponentView {
  companyId: string;
  public sections: SectionsItems[];
  private translator: BaseWebComponentTranslator;
  private donutPayload = {
    data: [],
    title: '',
  } as GeneralDonutChartInput;

  public getDomElements() {
    this.dom.$wrapper = this.shadow.querySelector('.wrapper');
    this.dom.$associatedRisksTitle = this.shadow.querySelector('#associated-risks-title');
    this.dom.$subtitleRisks = this.shadow.querySelector('#subtitle-risks');
    this.dom.$donutChart = this.shadow.querySelector('gz-general-donut-chart');
    this.dom.$eraSection = this.shadow.querySelector('#era-risks-section');
    this.dom.$cloudSecuritySection = this.shadow.querySelector('#cloud-security-risks-section');
    this.dom.$rootRisksSection = this.shadow.querySelector('#root-risks-section');
    this.dom.$viewAllRisksButton = this.shadow.querySelector('#view-all-risks-button');
    this.dom.$containerForSections = this.shadow.querySelector('#incident-risks-sections');
    this.getTranslations();
    this.setTranslatedStrings();
    this.sections = [
      { section: this.dom.$eraSection, key: RisksSectionsStrings.eraSection },
      { section: this.dom.$cloudSecuritySection, key: RisksSectionsStrings.cloudSecuritySection },
      { section: this.dom.$rootRisksSection, key: RisksSectionsStrings.rootCause },
    ];
  }

  private setNumberOfItemsToDisplay(): void {
    this.dom.$cloudSecuritySection.numberOfItemsToDisplay = CLOUD_SECURITY_NUMBER_OF_ITEMS;
    this.dom.$cloudSecuritySection.displayExceedingItems = false;
    this.dom.$eraSection.numberOfItemsToDisplay = ERA_NUMBER_OF_ITEMS;
    this.dom.$eraSection.displayExceedingItems = false;
    this.dom.$rootRisksSection.numberOfItemsToDisplay = ROOT_CAUSE_NUMBER_OF_ITEMS;
    this.dom.$rootRisksSection.displayExceedingItems = true;
  }

  public getTranslations(): any {
    this.translator = BaseWebComponentTranslator.getInstance();
    this.translations = this.stringsTranslator.getRiskIncidentPanelStrings();
    return this.translations;
  }

  public setTranslatedStrings(): void {
    this.dom.$associatedRisksTitle.innerHTML = htmlEncode(this.translations.panelTitle);
    this.dom.$subtitleRisks.innerText = this.translations.subtitlePanelText;
    this.dom.$viewAllRisksButton.innerText = this.translations.viewAllRisksText;
  }

  private noDataAvailableForDonutChart(sectionData: RisksSections): HTMLDivElement {
    const noDataAvailableDonutChartBody = document.createElement('div');
    noDataAvailableDonutChartBody.classList.add('status-chart-content');
    noDataAvailableDonutChartBody.appendChild(templateNoDataAvailableChart.content.cloneNode(true));
    const sectionDataCloudSecurity = sectionData.cloudSecurity as unknown as ErrorInterface;
    const noDataText = noDataAvailableDonutChartBody.querySelector('.no-data-text') as GzTextWebComponent;
    if (sectionDataCloudSecurity.errorCode && sectionDataCloudSecurity.errorCode === ErrorCodes.CSPM_LICENSE_NOT_FOUND) {
      noDataText.textContent = this.translations.noDataAvailableWithoutCspmLicense;
    } else {
      noDataText.textContent = this.translations.noDataAvailableWithCspmLicense;
    }
    return noDataAvailableDonutChartBody;
  }

  private errorLoadingDonutChart(): HTMLDivElement {
    const errorLoadingDonutChart = document.createElement('div');
    errorLoadingDonutChart.classList.add('status-chart-content');
    errorLoadingDonutChart.appendChild(templateErrorLoadingChart.content.cloneNode(true));
    const errorLoadingText = errorLoadingDonutChart.querySelector('.error-text') as GzTextWebComponent;
    errorLoadingText.textContent = this.translations.errorLoadingData;
    return errorLoadingDonutChart;
  }

  public buildSuccessView(data: any): void {
    this.companyId = data[1].companyId;
    this.buildSectionsWithSuccess(data[0], data[1].total);
    this.buildDonutChartWithSuccess(data[0], data[1].entities);
  }

  public buildErrorView(error: Error): void {
    this.dom.$viewAllRisksButton.disabled = true;
    this.buildDonutChartWithError();
    this.buildSectionsWithErrorState();
  }

  private buildSectionsWithSuccess(data: RisksSections, totalEntities: number): void {
    // start to build the sections
    this.setNumberOfItemsToDisplay();
    for (const sectionItem of this.sections) {
      // Specific case when the cspm+ license is required
      if (
        data[sectionItem.key].hasOwnProperty('errorCode') &&
        data[sectionItem.key]['errorCode'] === ErrorCodes.CSPM_LICENSE_NOT_FOUND
      ) {
        this.dom.$cloudSecuritySection.state = WidgetStateFrontEnd.IS_EMPTY;
        this.dom.$cloudSecuritySection.buttonProps = {
          buttonText: this.translations.getLicenseText,
          detailForEvent: CloudSecuritySectionEvents.CLICK_ON_GET_LICENSE,
        };
        this.dom.$cloudSecuritySection.statusMessage = {
          icon: 'ds-icon-notification_warning',
          firstText: this.translations.cloudSecurityLicenseRequired,
          secondText: this.translations.reasonForCloudSecurityLicenseRequired,
        };
        // We have no items in this section
      } else if (data[sectionItem.key].length === 0) {
        sectionItem.section.state = WidgetStateFrontEnd.IS_EMPTY;
        if (totalEntities > 0) {
          sectionItem.section.statusMessage = {
            icon: 'ds-icon-notification_warning',
            firstText: this.translator.getTranslatedRisksSections(sectionItem.key).noDataAvailable,
          };
        }
        // We have items in this section
      } else {
        sectionItem.section.state = WidgetStateFrontEnd.IS_ACTIVE;
      }
      sectionItem.section.payload = {
        data: data[sectionItem.key],
        title: this.translator.getTranslatedRisksSections(sectionItem.key).title,
      };
    }
  }

  private setMandatoryPropertiesDonut(): void {
    this.donutPayload.title = this.translations.donutChartTitle;
    this.dom.$donutChart.payload = this.donutPayload;
    this.dom.$donutChart.heightAndWidth = this.dom.$wrapper.offsetWidth;
  }

  private buildDonutChartWithError(): void {
    this.dom.$donutChart.state = WidgetStateFrontEnd.IS_ERROR;
    this.dom.$donutChart.errorLoadingData = this.errorLoadingDonutChart.bind(this);
    this.dom.$viewAllRisksButton.disabled = true;
    this.setMandatoryPropertiesDonut();
  }

  private buildSectionsWithErrorState(): void {
    this.setNumberOfItemsToDisplay();
    for (const sectionItem of this.sections) {
      sectionItem.section.state = WidgetStateFrontEnd.IS_ERROR;
    }
  }

  private buildDonutChartWithSuccess(sectionData: RisksSections, chartData: any): void {
    // The chart is empty
    if (chartData.length === 0) {
      this.dom.$donutChart.noDataAvailable = this.noDataAvailableForDonutChart.bind(this, sectionData);
      this.dom.$donutChart.state = WidgetStateFrontEnd.IS_EMPTY;
      this.dom.$viewAllRisksButton.disabled = true;
    } else {
      // build a pie chart for each entity
      for (const key in chartData) {
        if (chartData.hasOwnProperty(key)) {
          const entity = chartData[key];
          if (entity.total > 0) {
            const pieChart = {
              id: key,
              value: entity.total,
              label: Translator.translateXdrEntityType(key),
            };
            this.donutPayload.data.push(pieChart);
          }
        }
      }
      this.dom.$donutChart.state = WidgetStateFrontEnd.IS_ACTIVE;
    }
    this.setMandatoryPropertiesDonut();
  }
}
